import {useAuth} from '@apps-common/auth-react';
import PolygonIcon from '@apps-common/ui-icons/Polygon.jsx';
import LoopIcon from '@mui/icons-material/Loop';
import SettingsIcon from '@mui/icons-material/Settings';
import {Badge, Box, Divider as MUIDivider} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useListUploads} from '@api/asyncIngest';
import {queryKeys as collectionsQueryKeys} from '@api/collections';
import {showLeftDrawer} from '@app/actions/ui';
import {SideNav, ToggleButton} from '@app/components/common/SideNav';
import {DRAWERS} from '@app/constants';
import useOrgParams from '@app/hooks/useOrgParams';
import {isUsersOrg} from '@app/util/user';

const Divider = props => (
  <MUIDivider variant="middle" sx={{marginX: 1}} {...props} />
);

const LeftNav = () => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();
  const {user} = useAuth();
  const {orgId} = useOrgParams();
  const isUserOrg = isUsersOrg(user?.claims, orgId);

  const activeDrawer = useSelector(state => state.ui.drawers.left);

  const onToggleDrawer = (event, drawer) => {
    // if the drawer is active, clear it.
    dispatch(showLeftDrawer(activeDrawer === drawer ? '' : drawer));
  };

  // notify users when uploads need attention
  const {data: uploads} = useListUploads({enabled: isUserOrg});

  // simple state value to track if any of our uploads are currently migrating
  // to collections. We use this to trigger a refresh of the collections
  // query.
  const [previouslyMigrated, setPreviouslyMigrated] = useState(null);

  useEffect(() => {
    if (activeDrawer === DRAWERS.UPLOADS && !isUserOrg) {
      dispatch(showLeftDrawer(''));
    }
    [orgId, isUserOrg];
  });

  useEffect(() => {
    if (!uploads || !uploads?.migrated) {
      return;
    }

    // the first time, just set the baseline migrated count
    if (!previouslyMigrated) {
      setPreviouslyMigrated(uploads.migrated);
      return;
    }

    const migratedUploads = uploads.migrated;

    // check if the migrated uploads have changed
    if (
      migratedUploads?.length !== previouslyMigrated.length ||
      !migratedUploads.every((v, i) => {
        return previouslyMigrated[i] === v;
      })
    ) {
      setPreviouslyMigrated(uploads.migrated);
      queryClient.invalidateQueries({queryKey: collectionsQueryKeys.lists()});
    }
  }, [uploads, previouslyMigrated, queryClient]);

  return (
    <SideNav>
      <Box
        sx={{
          flexGrow: 1,
          zIndex: 'inherit',
        }}
      >
        <ToggleButton
          value={DRAWERS.COLLECTIONS}
          selected={activeDrawer === DRAWERS.COLLECTIONS}
          onClick={onToggleDrawer}
          title="Areas of interest"
          data-testid={DRAWERS.COLLECTIONS}
        >
          <PolygonIcon />
        </ToggleButton>
      </Box>

      <div>
        <Divider />

        {isUserOrg && (
          <ToggleButton
            value={DRAWERS.UPLOADS}
            selected={activeDrawer === DRAWERS.UPLOADS}
            onClick={onToggleDrawer}
            title="File Uploads"
          >
            <Badge badgeContent={uploads?.uploadsInProgress} color="error">
              <LoopIcon />
            </Badge>
          </ToggleButton>
        )}

        <ToggleButton
          value={DRAWERS.SETTINGS}
          selected={activeDrawer === DRAWERS.SETTINGS}
          onClick={onToggleDrawer}
          title="Adjust your settings"
        >
          <SettingsIcon />
        </ToggleButton>
      </div>
    </SideNav>
  );
};

export default LeftNav;
